import React, { useState, useEffect, useMemo } from 'react'
import { Info } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Alert, AlertDescription,
  Button, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../ui'
import { resolverJugadores, asignarPromocion } from '../../../../lib/supabase'
import { formatMoney } from '../../../../utils/helpers'

const ETIQUETA_UNIDAD = { grupo: 'grupo(s)', familia: 'familia(s)', jugador: 'jugador(es)' }

// Que descuento otorga una promocion, en palabras. El de hermanos no tiene un
// numero unico: depende de que hijo sea cada quien, y decir "10%" seria mentir.
const descripcionDescuento = (promo) => {
  if (!promo) return ''
  if (promo.tipo === 'hermano') {
    const c = promo.config || {}
    const partes = []
    if (c.hijo_2) partes.push(`2do ${c.hijo_2}%`)
    if (c.hijo_3) partes.push(`3ro ${c.hijo_3}%`)
    if (c.hijo_4) partes.push(`4to ${c.hijo_4}%`)
    return partes.length
      ? `Según el hijo que sea cada quien: ${partes.join(' · ')}. Al primero no se le descuenta.`
      : 'Por posición del hijo en la familia.'
  }
  if (promo.porcentaje) return `${promo.porcentaje}% de descuento.`
  // Un monto fijo no sabe a cual cargo pertenece: se otorga como saldo a favor y
  // el admin decide a que se aplica. Llamarlo "descuento" prometia otra cosa.
  if (promo.monto_fijo) return `${formatMoney(promo.monto_fijo)} de saldo a favor.`
  return 'Sin descuento configurado.'
}

// Hermano del dialogo de planes. Asignar una promocion SI mueve dinero, asi que
// antes de escribir se pregunta al motor que va a pasar y se enseña: cuantos
// cargos bajan, cuanto suman, y cuales no se tocan por tener pagos.
const AsignarPromocionDialog = ({
  open, onOpenChange, unidad, ids = [], temporadaId, promociones = [], onDone,
}) => {
  const [elegida, setElegida] = useState('')
  const [preview, setPreview] = useState(null)
  const [impacto, setImpacto] = useState(null)
  const [aplicando, setAplicando] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) { setElegida(''); setPreview(null); setImpacto(null); setError(null) }
  }, [open])

  // Se resuelve ANTES de escribir: el admin ve a cuantos alcanza.
  useEffect(() => {
    if (!open || !elegida) { setPreview(null); setImpacto(null); setError(null); return }
    let vivo = true
    setPreview(null)
    setImpacto(null)
    setError(null)
    resolverJugadores({ unidad, ids, temporadaId })
      .then(async (jugadorIds) => {
        if (!vivo) return
        setPreview({ jugadorIds })
        // Preguntar sin escribir: el mismo servicio que va a aplicar el cambio
        // devuelve el resumen, asi que lo que se enseña no puede discrepar de lo
        // que pasa al confirmar.
        const { data } = await asignarPromocion({ promocionId: elegida, jugadorIds, dryRun: true })
        if (vivo) setImpacto(data || null)
      })
      .catch((e) => { if (vivo) setError(e.message || 'No se pudo calcular a quién aplica.') })
    return () => { vivo = false }
  }, [open, elegida, unidad, ids, temporadaId])

  const promo = useMemo(
    () => promociones.find((p) => p.id === elegida) || null,
    [promociones, elegida],
  )

  const totalRebajado = useMemo(
    () => (impacto?.resumen?.cambios || []).reduce((s, c) => s + (Number(c.antes || 0) - Number(c.despues || 0)), 0),
    [impacto],
  )

  const sinEfecto = !!impacto
    && !impacto.resumen?.cambios?.length
    && !impacto.resumen?.historia?.length
    && !(impacto.montoBeca > 0 && impacto.becas > 0)

  const confirmar = async () => {
    setAplicando(true)
    setError(null)
    try {
      const { error: err, data } = await asignarPromocion({
        promocionId: elegida, jugadorIds: preview.jugadorIds,
      })
      if (err) { setError(err.message || String(err)); return }
      onDone?.({
        jugadores: data?.asignados ?? preview.jugadorIds.length,
        promocion: promo?.nombre,
        resumen: data?.resumen || null,
        becas: data?.becas || 0,
        montoBeca: data?.montoBeca || 0,
      })
    } catch (e) {
      setError(e.message || 'No se pudo asignar la promoción.')
    } finally {
      // Sin esto, un rechazo dejaria `aplicando` en true para siempre y el
      // dialogo no cerraria ni con Escape (ver manejarOpenChange).
      setAplicando(false)
    }
  }

  const manejarOpenChange = (siguiente) => { if (!aplicando) onOpenChange(siguiente) }

  const etiquetaUnidad = ETIQUETA_UNIDAD[unidad] || 'seleccionado(s)'

  return (
    <Dialog open={open} onOpenChange={manejarOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Asignar promoción</DialogTitle></DialogHeader>

        <div className="space-y-3">
          <div>
            <Label>Promoción</Label>
            <Select value={elegida} onValueChange={setElegida}>
              <SelectTrigger><SelectValue placeholder="Seleccionar promoción" /></SelectTrigger>
              <SelectContent>
                {promociones.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {promociones.length === 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                No hay promociones activas en esta temporada. Créalas en Pagos → Promociones.
              </p>
            )}
          </div>

          {promo && !preview && <p className="text-sm text-muted-foreground">Calculando a quién aplica...</p>}

          {promo && preview && (
            <div className="rounded-md border border-border p-3 text-sm">
              <p>
                <strong>{ids.length}</strong> {etiquetaUnidad} → se le otorgará a{' '}
                <strong>{preview.jugadorIds.length}</strong> jugador(es) inscrito(s).
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{descripcionDescuento(promo)}</p>
            </div>
          )}

          {promo && preview && !impacto && (
            <p className="text-sm text-muted-foreground">Calculando el efecto en sus cargos...</p>
          )}

          {impacto && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs space-y-1">
                {impacto.resumen?.cambios?.length > 0 && (
                  <p>
                    <strong>{impacto.resumen.cambios.length}</strong> cargo(s) bajan de precio:{' '}
                    <strong>−{formatMoney(totalRebajado)}</strong> en total.
                  </p>
                )}
                {impacto.resumen?.historia?.length > 0 && (
                  <p>
                    <strong>{impacto.resumen.historia.length}</strong> cargo(s) ya tienen pagos: no se
                    tocan, y el descuento les llega como saldo a favor.
                  </p>
                )}
                {impacto.resumen?.saldoGenerado > 0 && (
                  <p>Se genera <strong>{formatMoney(impacto.resumen.saldoGenerado)}</strong> en saldo a favor.</p>
                )}
                {impacto.montoBeca > 0 && impacto.becas > 0 && (
                  <p>
                    Beca: <strong>{formatMoney(impacto.montoBeca)}</strong> de saldo a favor
                    para {impacto.becas} jugador(es).
                  </p>
                )}
                {sinEfecto && (
                  <p>
                    No hay cargos por ajustar. Se aplicará a los cargos que se generen después.
                  </p>
                )}
              </AlertDescription>
            </Alert>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => manejarOpenChange(false)} disabled={aplicando}>
            Cancelar
          </Button>
          <Button
            onClick={confirmar}
            disabled={!promo || !preview?.jugadorIds.length || aplicando}
          >
            {aplicando ? 'Asignando...' : `Asignar a ${preview?.jugadorIds.length || 0}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default AsignarPromocionDialog
